import type React from "react";
import { useEffect, useId, useState } from "react";
import { cn } from "../../../../../../../util/classNameConstructor";
import Button from "../../../../../../core/button/Button";
import ColorPicker from "../../../../../../core/inputs/ColorPicker";
import {
  Combobox,
  type ComboboxOption,
} from "../../../../../../core/inputs/Combobox";
import Select from "../../../../../../core/select/Select";
import Seperator from "../../../../../../core/seperator/Seperator";
import LabelledSwitch from "../../../../../../core/switch/LabelledSwitch";
import type { ImageryStyle } from "../cesium-types";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface SettingsPanelProps {
  onDepthTestChange: (enabled: boolean) => void;
  onShadowsChange: (enabled: boolean) => void;
  onImageryStyleChange: (style: ImageryStyle) => void;
  onPolygonDrawingChange: (enabled: boolean) => void;
  onOsmBuildingsChange: (enabled: boolean) => void;
  onOsmBuildingColorChange: (color: string, opacity: number) => void;
  imageryStyle: ImageryStyle;
  osmBuildingsEnabled: boolean;
  osmBuildingColor: string;
  conditions: StyleCondition[];
  onStyleConditionsChange: (conditions: StyleCondition[]) => void;
  osmBuildingOpacity: number;
  geoidHeightDifference?: number | null;
  onGeoidHeightDifferenceChange?: (height: number | null) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onDepthTestChange,
  onShadowsChange,
  onImageryStyleChange,
  onPolygonDrawingChange,
  onOsmBuildingsChange,
  onOsmBuildingColorChange,
  imageryStyle,
  osmBuildingsEnabled,
  osmBuildingColor,
  conditions,
  onStyleConditionsChange,
  osmBuildingOpacity,
  geoidHeightDifference,
  onGeoidHeightDifferenceChange,
}) => {
  const [depthTestEnabled, setDepthTestEnabled] = useState(false);
  const [shadowsEnabled, setShadowsEnabled] = useState(true);
  const [geoidHeightInput, setGeoidHeightInput] = useState<string>(
    geoidHeightDifference?.toString() || "0",
  );
  const geoidHeightInputId = useId();

  const handleDepthTestChange = (enabled: boolean) => {
    setDepthTestEnabled(enabled);
    onDepthTestChange(enabled);
  };

  const handleShadowsChange = (enabled: boolean) => {
    setShadowsEnabled(enabled);
    onShadowsChange(enabled);
  };

  const handleImageryStyleChange = (option: ComboboxOption) => {
    if (typeof option.value === "string") {
      onImageryStyleChange(option.value as ImageryStyle);
    }
  };

  const handleRemoveCondition = (index: number) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    onStyleConditionsChange(updatedConditions);
  };

  const handleColorChange = (index: number, color: string, opacity: number) => {
    const updatedConditions = conditions.map((condition, i) => {
      if (i === index) {
        return { ...condition, color, opacity };
      }
      return condition;
    });
    onStyleConditionsChange(updatedConditions);
  };

  const handleToggleVisibility = (index: number) => {
    const condition = conditions[index];
    const newOpacity = condition.opacity === 0 ? 1.0 : 0;
    handleColorChange(index, condition.color, newOpacity);
  };

  const handleGeoidHeightChange = (value: string) => {
    setGeoidHeightInput(value);

    if (value === "") {
      // Empty string means revert to automatic calculation (null)
      if (onGeoidHeightDifferenceChange) {
        onGeoidHeightDifferenceChange(null);
      }
    } else {
      const numValue = Number.parseFloat(value);
      if (!isNaN(numValue) && onGeoidHeightDifferenceChange) {
        onGeoidHeightDifferenceChange(numValue);
      }
    }
  };

  // Update local input when prop changes
  useEffect(() => {
    if (geoidHeightDifference !== undefined) {
      setGeoidHeightInput(geoidHeightDifference?.toString() || "0");
    }
  }, [geoidHeightDifference]);

  return (
    <div className="space-y-3">
      <Combobox
        enableSearch={false}
        size="lg"
        options={[
          { value: "openstreetmap", label: "OpenStreetMap" },
          { value: "satellite", label: "Satellite" },
          { value: "satellite-buildings", label: "Satellite + Buildings" },
          { value: "photorealistic", label: "Photorealistic" },
          // { value: "maptiler-streets", label: "MapTiler Streets" },
          // { value: "maptiler-basic", label: "MapTiler Basic" },
          // { value: "maptiler-bright", label: "MapTiler Bright" },
          // { value: "maptiler-pastel", label: "MapTiler Pastel" },
          // { value: "maptiler-topo", label: "MapTiler Topo" },
          // { value: "maptiler-toner", label: "MapTiler Toner" },
          // { value: "maptiler-hybrid", label: "MapTiler Hybrid" },
          // { value: "maptiler-winter", label: "MapTiler Winter" },
          // { value: "maptiler-positron", label: "MapTiler Positron" },
          { value: "mapbox-streets", label: "Mapbox Streets" },
          { value: "mapbox-outdoors", label: "Mapbox Outdoors" },
          // { value: "mapbox-light", label: "Mapbox Light" },
          // { value: "mapbox-dark", label: "Mapbox Dark" },
          { value: "mapbox-satellite", label: "Mapbox Satellite" },
          {
            value: "mapbox-satellite-streets",
            label: "Mapbox Satellite Streets",
          },
          { value: "mapbox-custom", label: "Mapbox Custom Style" },
        ]}
        value={imageryStyle}
        onChange={handleImageryStyleChange}
      />

      {/* Geoid Height Difference Input */}
      {geoidHeightDifference !== undefined && (
        <div className="space-y-2">
          <label
            htmlFor={geoidHeightInputId}
            className="text-sm font-medium text-text-default"
          >
            Geoid Height Difference (m)
          </label>
          <div className="flex gap-2">
            <input
              id={geoidHeightInputId}
              type="number"
              step="0.1"
              value={geoidHeightInput}
              onChange={(e) => handleGeoidHeightChange(e.target.value)}
              className="flex-1 px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder={
                geoidHeightDifference === null
                  ? "Auto-calculated"
                  : "Enter height difference in meters"
              }
            />
            <button
              type="button"
              onClick={() => handleGeoidHeightChange("")}
              className="px-3 py-2 text-sm font-medium text-text-subdued border border-border-default rounded-md hover:bg-surface-hover hover:text-text-default transition-colors"
              title="Revert to automatic calculation"
            >
              {geoidHeightDifference === null ? "Auto" : "Reset"}
            </button>
          </div>
          <p className="text-xs text-text-subdued">
            Calculated: {geoidHeightDifference?.toFixed(1) || "Not set"}m •
            Adjust to fix underground models
          </p>
        </div>
      )}

      <div>
        <LabelledSwitch
          name="osmBuildings"
          prefix={
            osmBuildingsEnabled && (
              <ColorPicker
                initialColor={osmBuildingColor}
                initialOpacity={osmBuildingOpacity}
                handleSave={async (color, opacity) => {
                  await Promise.resolve(); // Ensure async behavior
                  onOsmBuildingColorChange(color, opacity);
                }}
              />
            )
          }
          label="OSM Gebäude"
          toggled={osmBuildingsEnabled}
          handleSetToggle={onOsmBuildingsChange}
        />

        <LabelledSwitch
          name="depthTest"
          label="Erdboden transparent"
          toggled={!depthTestEnabled}
          handleSetToggle={handleDepthTestChange}
        />

        <LabelledSwitch
          name="shadows"
          label="Schatten"
          toggled={shadowsEnabled}
          handleSetToggle={handleShadowsChange}
        />
      </div>

      {/* <Button
          text="Draw Polygon"
          onClick={() => onPolygonDrawingChange(true)}
          className="w-full"
        /> */}
    </div>
  );
};

export default SettingsPanel;
